/**
 * Feature Interaction Heatmap Component
 * 
 * Heatmap showing interaction strength between features.
 * Helps identify which features work together in predictions.
 * 
 * Following BRAIN-DESIGN-SYSTEM.md:
 * - Color intensity based on interaction strength
 * - Proper grid and cell styling
 * - Tooltips for detailed information
 */

import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

interface InteractionHeatmapProps {
  features: string[];
  interactions: number[][]; // 2D matrix of interaction strengths (0-1)
}

const getColorForValue = (value: number): string => {
  if (value < 0.2) return '#1F2937'; // Low - dark gray
  if (value < 0.4) return '#374151'; // Medium-low - gray
  if (value < 0.6) return '#3B82F6'; // Medium - blue
  if (value < 0.8) return '#8B5CF6'; // Medium-high - purple
  return '#A855F7'; // High - bright purple
};

const getIntensityLabel = (value: number): string => {
  if (value < 0.2) return 'Low';
  if (value < 0.4) return 'Medium-Low';
  if (value < 0.6) return 'Medium';
  if (value < 0.8) return 'Medium-High';
  return 'High';
};

export const InteractionHeatmap: React.FC<InteractionHeatmapProps> = ({
  features,
  interactions
}) => {
  const { maxInteraction, avgInteraction } = useMemo(() => {
    // Validate interactions is a valid 2D array
    if (!interactions || !Array.isArray(interactions) || interactions.length === 0) {
      return { maxInteraction: 0, avgInteraction: 0 };
    }
    
    let max = 0;
    let sum = 0;
    let count = 0;
    
    interactions.forEach((row, i) => {
      // Skip invalid rows
      if (!Array.isArray(row)) return;
      
      row.forEach((value, j) => {
        if (i !== j) { // Exclude diagonal
          max = Math.max(max, value);
          sum += value;
          count++;
        }
      });
    });
    
    return {
      maxInteraction: max,
      avgInteraction: count > 0 ? sum / count : 0
    };
  }, [interactions]);

  const cellSize = Math.min(60, Math.floor(600 / features.length));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
        Feature Interactions
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Features
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {features.length} × {features.length}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Max Interaction
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {maxInteraction.toFixed(3)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Avg Interaction
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {avgInteraction.toFixed(3)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 600 }}>
        <Box sx={{ display: 'inline-block', minWidth: 'fit-content' }}>
          {/* Column headers */}
          <Box sx={{ display: 'flex', ml: `${cellSize}px` }}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  width: cellSize,
                  height: cellSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    transform: 'rotate(-45deg)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: cellSize
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Heatmap grid */}
          {features.map((rowFeature, rowIndex) => (
            <Box key={rowIndex} sx={{ display: 'flex' }}>
              {/* Row header */}
              <Box
                sx={{
                  width: cellSize,
                  height: cellSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: cellSize - 8
                  }}
                >
                  {rowFeature}
                </Typography>
              </Box>

              {/* Cells */}
              {interactions[rowIndex].map((value, colIndex) => (
                <Tooltip
                  key={colIndex}
                  title={
                    <Box>
                      <Typography variant="caption" display="block">
                        {rowFeature} × {features[colIndex]}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Strength: {value.toFixed(3)} ({getIntensityLabel(value)})
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box
                    sx={{
                      width: cellSize,
                      height: cellSize,
                      bgcolor: getColorForValue(value),
                      border: '1px solid #111827',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        zIndex: 10,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                      }
                    }}
                  >
                    {rowIndex === colIndex && (
                      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.6rem' }}>
                        —
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Interaction Strength:
        </Typography>
        {[
          { label: 'Low (0.0-0.2)', color: '#1F2937' },
          { label: 'Medium-Low (0.2-0.4)', color: '#374151' },
          { label: 'Medium (0.4-0.6)', color: '#3B82F6' },
          { label: 'Medium-High (0.6-0.8)', color: '#8B5CF6' },
          { label: 'High (0.8-1.0)', color: '#A855F7' }
        ].map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: item.color, borderRadius: 0.5, border: '1px solid #374151' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          <strong>Insight:</strong> Darker colors indicate weaker interactions, while brighter purple colors show stronger feature interactions. 
          Diagonal cells (feature with itself) are excluded from analysis.
        </Typography>
      </Box>
    </Box>
  );
};

export default InteractionHeatmap;
